import { defineStore } from 'pinia';
import { Chat, type Message } from '@ai-sdk/vue';
import { DefaultChatTransport } from 'ai';
import { useSettingStore } from '@@/stores/useSettingStore'
import { getTextFromMessage } from '@nuxt/ui/utils/ai';

// Interface for serializable chat data
export interface ChatData {
  id: string;
  messages: Message[];
  createdAt: Date;
  updatedAt: Date;
  label?: string;
  assistantId?: string;
  group?: string;
  pinned?: boolean;
  status?: 'active' | 'archived' | 'deleted';
}

export const useChatStore = defineStore('chat', {
  state: () => ({
    chats: [] as ChatData[], // Store plain objects
    activeChatId: null as string | null, // ID of the active chat
    renamingChatId: null as string | null, // ID of the chat being renamed
    activeChatInstances: new Map<string, Chat>(), // Cache of active Chat instances (not persisted)
    isInitialized: false, // Track if we've loaded from database
    isInitializing: false, // Prevent multiple initialization calls
    // Track resolved mode info per chat from streaming metadata
    resolvedModeByChat: {} as Record<string, {
      selectedMode: 'auto' | 'fast' | 'expert'
      resolvedMode: 'fast' | 'expert'
      reason?: string
      timestamp: number
    }>,
  }),
  
  getters: {
    
    // Get the active chat object
    activeChat(state): ChatData | null {
      if (!state.activeChatId) return null;
      return state.chats.find(chat => chat.id === state.activeChatId) || null;
    },

    

    // Get the active Chat instance
    activeChatInstance(state): Chat | null {
      if (!state.activeChatId) return null;
      return state.activeChatInstances.get(state.activeChatId) || null;
    },
    
    // Get a chat by ID (as a getter with parameter)
    getChat: (state) => {
      return (id: string): ChatData | undefined => {
        return state.chats.find(chat => chat.id === id);
      };
    },

    // Get chats with automatic initialization - now more aggressive
    chatsWithInit(state): ChatData[] {
      // Always ensure initialization happens immediately
      const store = useChatStore()
      if (!state.isInitialized && !state.isInitializing) {
        // Start initialization immediately (don't wait for setTimeout)
        store.ensureInitialized().catch(error => {
          console.error('[Chat Store] Failed to initialize in getter:', error)
        })
        // Return empty array until initialization completes
        return []
      }
      return state.chats
    },

    // Add a computed property that forces initialization
    allChats(state): ChatData[] {
      // Trigger initialization when chats are accessed
      const store = useChatStore()
      store.ensureInitializedSync()
      return state.chats
    }
  },
  
  actions: {
    // Record resolved mode info for a chat
    setResolvedMode(id: string, data: { selectedMode: 'auto' | 'fast' | 'expert'; resolvedMode: 'fast' | 'expert'; reason?: string }) {
      this.resolvedModeByChat[id] = { ...data, timestamp: Date.now() }
    },
    // ==================== INITIALIZATION ====================
    
    /**
     * Initialize chat store by loading sessions from database
     * This is now the ONLY source of truth for chat data
     */
    async initializeFromDatabase() {
      // Prevent multiple initialization calls
      if (this.isInitialized || this.isInitializing) {
        return this.isInitialized
      }

      this.isInitializing = true
      
      try {
        console.log('[Chat Store] Initializing chat sessions from database...')
        
        // Load ALL chat sessions for the current user, not just a specific one
        const response = await $fetch('/api/chats/sessions', {
          method: 'GET',
          headers: { 'cache-control': 'no-store' }
        })
        
        if (response?.sessions) {
          // Convert database sessions to ChatData format
          const chatData: ChatData[] = response.sessions.map((session: any) => ({
            id: session.id,
            messages: [], // Messages will be loaded on-demand when chat is opened
            createdAt: new Date(session.createdAt),
            updatedAt: new Date(session.updatedAt),
            label: session.label,
            assistantId: session.assistantId,
            group: session.group,
            pinned: session.pinned || false,
            starred: session.starred || false,
            status: session.status || 'active'
          }))
          
          // Replace the entire chats array with database data (database is authoritative)
          this.chats = chatData
          
          console.log(`[Chat Store] Loaded ${chatData.length} sessions from database`)
        } else {
          this.chats = []
          console.log('[Chat Store] No chat sessions found in database')
        }
        
        this.isInitialized = true
        return true
      } catch (error) {
        console.error('[Chat Store] Failed to initialize from database:', error)
        // Initialize with empty array if database fails
        this.chats = []
        this.isInitialized = true // Don't keep retrying if DB is unavailable
        return false
      } finally {
        this.isInitializing = false
      }
    },

    /**
     * Ensure the store is initialized from database before accessing chats
     * This handles both fresh loads and page refreshes
     */
    async ensureInitialized() {
      // If we're already initialized or currently initializing, return
      if (this.isInitialized && !this.isInitializing) {
        return this.isInitialized
      }
      
      // If we have chats from localStorage but aren't initialized, 
      // we need to sync with database
      await this.initializeFromDatabase()
      return this.isInitialized
    },

    // ==================== CHAT ITEMS GETTER ====================
    
    // Convert chats to conversation items format (database-first)
    async getChatItems() {
      // ALWAYS ensure we're loading from database first
      await this.ensureInitialized()
      
      const { getAssistantById } = useAssistants();
      return [
        {type: 'label', label: 'Chats'} ,
        ...[...this.chats].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()).map(chat => ({
        key: chat.id,
        label: chat.label,
        assistantId: chat.assistantId || 'kira',
        avatar: {
          src: getAssistantById(chat.assistantId)?.icon,
          size: 'xs'
        },
        chatId: chat.id,
        to: `/assistants/${chat.assistantId || 'kira'}/chats/${chat.id}`,
        timestamp: chat.updatedAt,
        disabled: false,
        group: chat.group,
        menuItems: [
          {
            key: 'share-chat',
            label: 'Share',
            icon: 'i-lucide-share',
            onClick: () => {
              this.shareChat(chat.id);
            }
          },
          {
            key: 'rename-chat',
            label: 'Rename',
            icon: 'i-lucide-pencil',
            onClick: () => {
              this.renamingChatId = chat.id;
            }
          },
          {
            key: 'add-to-project',
            label: 'Add to project',
            icon: 'i-lucide-folder-plus',
            children: [] // Placeholder for projects
          },
          {
            type: 'separator'
          },
          {
            key: 'archive-chat',
            label: 'Archive',
            icon: 'i-lucide-archive',
            onClick: () => this.archiveChat(chat.id)
          },
          {
            key: 'export-chat',
            label: 'Export',
            icon: 'i-lucide-download',
            onClick: () => this.exportChat(chat.id)
          },
          {
            key: 'delete-chat',
            label: 'Delete',
            icon: 'i-lucide-trash-2',
            color: 'error',
            onClick: () => this.deleteChat(chat.id)
          }
        ]
      }))];
    },

    // Synchronous version for immediate access (may be empty until initialization)
    getChatItemsSync() {
      // Trigger initialization in background if not already done
      if (!this.isInitialized && !this.isInitializing) {
        this.ensureInitialized().catch(error => {
          console.error('[Chat Store] Background initialization failed:', error)
        })
      }
      
      const { getAssistantById } = useAssistants();
      return [
        {type: 'label', label: 'Chats'} ,
        ...[...this.chats].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()).map(chat => ({
        key: chat.id,
        label: chat.label,
        assistantId: chat.assistantId || 'kira',
        avatar: {
          src: getAssistantById(chat.assistantId)?.icon,
          size: 'xs'
        },
        chatId: chat.id,
        to: `/assistants/${chat.assistantId || 'kira'}/chats/${chat.id}`,
        timestamp: chat.updatedAt,
        disabled: false,
        group: chat.group,
        menuItems: [
          {
            key: 'share-chat',
            label: 'Share',
            icon: 'i-lucide-share',
            onClick: () => {
              this.shareChat(chat.id);
            }
          },
          {
            key: 'rename-chat',
            label: 'Rename',
            icon: 'i-lucide-pencil',
            onClick: () => {
              this.renamingChatId = chat.id;
            }
          },
          {
            key: 'add-to-project',
            label: 'Add to project',
            icon: 'i-lucide-folder-plus',
            children: [] // Placeholder for projects
          },
          {
            type: 'separator'
          },
          {
            key: 'archive-chat',
            label: 'Archive',
            icon: 'i-lucide-archive',
            onClick: () => this.archiveChat(chat.id)
          },
          {
            key: 'export-chat',
            label: 'Export',
            icon: 'i-lucide-download',
            onClick: () => this.exportChat(chat.id)
          },
          {
            key: 'delete-chat',
            label: 'Delete',
            icon: 'i-lucide-trash-2',
            color: 'error',
            onClick: () => this.deleteChat(chat.id)
          }
        ]
      }))];
    },

    // ==================== CHAT LIFECYCLE OPERATIONS ====================
    
    // Generate a unique chat label that avoids duplicates
    _generateUniqueLabel(): string {
      const existingLabels = new Set(this.chats.map(chat => chat.label));
      let counter = 1;
      let label = `New Chat`;
      
      // If "New Chat" exists, try numbered versions
      while (existingLabels.has(label)) {
        counter++;
        label = `New Chat ${counter}`;
      }
      
      return label;
    },

    // Create a new chat with a unique ID and return the instance
    async createChat(assistantId: string) {
      const chatInstance = this._createChatInstance(assistantId);

      // Extract serializable data and add to the store
      const chatData: ChatData = {
        id: chatInstance.id,
        messages: [...chatInstance.messages], // Clone messages array
        createdAt: chatInstance.createdAt,
        updatedAt: chatInstance.createdAt, // Initially same as createdAt
        label: this._generateUniqueLabel(),
        assistantId: assistantId,
        status: 'active',
      };

      this.chats.unshift(chatData);
      this.activeChatId = chatData.id;
      this.activeChatInstances.set(chatData.id, chatInstance);

      // Insert session into database
      try {
        await this._persistChatSession(chatData);
        console.log(`[Chat Store] Chat session successfully persisted to database: ${chatData.id}`)
      } catch (error) {
        console.error(`[Chat Store] Failed to persist chat session ${chatData.id}:`, error)
      }

      return chatInstance; // Return the full instance for immediate use
    },

    // Set active chat
    setActiveChat(id: string) {
      this.activeChatId = id;
    },

    // Toggle pin status for a chat
    async togglePin(id: string) {
      const chat = this.chats.find(c => c.id === id)
      if (!chat) return

      const newPinnedState = !chat.pinned
      
      // Optimistic update
      chat.pinned = newPinnedState
      chat.updatedAt = new Date()
      
      // Persist to server
      try {
        await $fetch(`/api/chats/${id}`, {
          method: 'PATCH',
          body: { pinned: newPinnedState }
        })
        console.log(`Chat ${id} ${newPinnedState ? 'pinned' : 'unpinned'} successfully`)
      } catch (err) {
        console.error('[Chat Store] Failed to persist pin status:', err)
        // Revert optimistic update on failure
        chat.pinned = !newPinnedState
        chat.updatedAt = new Date()
        throw err
      }
    },

    // Delete a chat by ID
    async deleteChat(id: string) {
      const index = this.chats.findIndex(chat => chat.id === id);
      if (index === -1) return;

      // Store reference for potential rollback
      const deletedChat = this.chats[index];
      
      // Optimistically remove from local store
      this.chats.splice(index, 1);
      // Clear resolved mode cache for this chat
      if (this.resolvedModeByChat[id]) {
        delete this.resolvedModeByChat[id]
      }
      
      // Clear cached instance
      this.clearChatInstanceCache(id);
      
      // If we're deleting the active chat, clear the active chat ID
      if (this.activeChatId === id) {
        this.activeChatId = null;
      }
      
      // If we're deleting the chat being renamed, clear the renaming state
      if (this.renamingChatId === id) {
        this.renamingChatId = null;
      }

      // Delete from database
      try {
        await $fetch(`/api/chats/${id}`, {
          method: 'DELETE'
        })
        console.log(`[Chat Store] Successfully deleted chat ${id} from database`)
      } catch (error) {
        console.error(`[Chat Store] Failed to delete chat ${id} from database:`, error)
        // Rollback optimistic deletion on failure
        this.chats.splice(index, 0, deletedChat)
        // Restore active chat if it was the deleted one
        if (this.activeChatId === null && deletedChat.id === id) {
          this.activeChatId = id
        }
        throw error
      }
    },

    // ==================== CHAT LIST OPERATIONS ====================
    
    // Add an existing chat from its data
    addChatData(chat: ChatData) {
      const existingChat = this.chats.find(c => c.id === chat.id);
      if (!existingChat) {
        this.chats.unshift(chat);
      }
    },

    // Update chats list
    setChatList(chats: ChatData[]) {
      this.chats = chats;
    },

    // ==================== CHAT INSTANCE MANAGEMENT ====================
    
    // Get or create a Chat instance for a given chat ID
    async getOrCreateChatInstance(chatId: string, assistantId: string): Promise<Chat | null> {      
      // Return cached instance if it exists
      if (this.activeChatInstances.has(chatId)) {
        return this.activeChatInstances.get(chatId)!;
      }

      // Find the chat data
      // let chatData = this.chats.find(chat => chat.id === chatId);
      let chatData = null

      // loading chat data from database
      try {
        const { useChatPersistence } = await import('@/composables/useChatPersistence')
        const { loadMessages } = useChatPersistence()
        const persistedData = await loadMessages(chatId)

        if (persistedData.messages && persistedData.messages.length > 0) {
          console.log(`[Chat Store] Loaded ${persistedData.messages.length} persisted messages for chat ${chatId}`)

          // Create chat data from persisted messages
          chatData = {
            id: chatId,
            messages: persistedData.messages,
            label: persistedData.metadata?.label || 'New Chat',
            description: persistedData.metadata?.description,
            createdAt: persistedData.metadata?.createdAt ? new Date(persistedData.metadata.createdAt) : new Date(),
            updatedAt: persistedData.metadata?.updatedAt ? new Date(persistedData.metadata.updatedAt) : new Date(),
            assistantId,
            status: 'active'
          }

          // Add to local store
          this.addChatData(chatData);
        } else {
          // No persisted messages, create new chat data
          chatData = this.getChat(chatId);
          if (!chatData) {
            console.warn(`[Chat Store] No chat data found for ${chatId}, returning null`)
            return null;
          }
        }
      } catch (error) {
        console.error('[Chat Store] Failed to load persisted messages:', error)
        // Fall back to existing behavior
        chatData = this.getChat(chatId);
        if (!chatData) {
          return null;
        }
      }

      if (!chatData) {
        return null;
      }

      // Create and cache the instance
      const chatInstance = this._createChatInstance(assistantId, chatData);
      this.activeChatInstances.set(chatId, chatInstance);

      return chatInstance;
    },

    // Clear cached Chat instance (useful for cleanup)
    clearChatInstanceCache(id: string) {
      this.activeChatInstances.delete(id);
    },

    // Private method to create Chat instances with consistent configuration
    _createChatInstance(assistantId: string, existingData?: ChatData): Chat {
      // Capture store reference for use in callbacks
      const store = this;
      // Read persisted search mode from settings (lowercase: 'auto' | 'fast' | 'expert')
      const settings = useSettingStore() as any
      const persistedMode = (settings?.searchMode || 'fast') as 'auto' | 'fast' | 'expert'
      
      const chatInstance = new Chat({
        ...(existingData ? {
          id: existingData.id,
          messages: [...existingData.messages],
          createdAt: existingData.createdAt
        } : {
          messages: []
        }),
        transport: new DefaultChatTransport({
          api: `/api/assistants/${assistantId}/chat`,
          body: existingData ? {
            chatId: existingData.id,
            mode: persistedMode,
          } : {
            mode: persistedMode,
          }
        }),
        onNewMessage(event) {
          // 📨 Log when first response chunk arrives (calculate from request start)
          this._firstChunkTime = performance.now()
          const timeToFirstChunk = this._firstChunkTime - this._requestStartTime
          console.log(`📨 [${new Date().toISOString()}] First response chunk received for chat ${this.id} (${timeToFirstChunk.toFixed(2)}ms after request)`) 
          
          console.log("New message event", this)
          // Save messages on every new message to ensure persistence
          // useChatStore().saveChatMessages(this.id, this.messages);

          // Try to capture resolved mode as soon as it appears in parts
          try {
            const lastAssistant = [...this.messages].reverse().find((m: any) => m.role === 'assistant') as any
            const modePart = lastAssistant?.parts?.find((p: any) => p?.type === 'data-mode')
            if (modePart?.data?.resolvedMode) {
              store.setResolvedMode(this.id, {
                selectedMode: (modePart.data.selectedMode || 'auto') as any,
                resolvedMode: modePart.data.resolvedMode as any,
                reason: modePart.data.reason,
              })
            }
          } catch (e) {
            console.debug('[Chat Store] Unable to parse data-mode part on new message')
          }
        },
        async onFinish() {
          // 🏁 Calculate and log total response time
          const finishTime = performance.now()
          // if (this._requestStartTime) {
          const totalTime = finishTime - this._requestStartTime
          const generationTime = this._firstChunkTime ? finishTime - this._firstChunkTime : 0
          console.log(`🏁 [${new Date().toISOString()}] AI response completed for chat ${this.id}`)
          console.log(`⏱️ Total response time: ${totalTime.toFixed(2)}ms`)
          console.log(`🧠 Generation time: ${generationTime.toFixed(2)}ms`)
          console.log(`🌐 Network/API time: ${this._firstChunkTime ? (this._firstChunkTime - this._requestStartTime).toFixed(2) : 'N/A'}ms`)
          
          // Reset timing for next request
          this._requestStartTime = undefined
          this._firstChunkTime = undefined
          
          console.log("Finish event", this)

          // Ensure resolved mode is captured at the end as well
          try {
            const lastAssistant = [...this.messages].reverse().find((m: any) => m.role === 'assistant') as any
            const modePart = lastAssistant?.parts?.find((p: any) => p?.type === 'data-mode')
            if (modePart?.data?.resolvedMode) {
              store.setResolvedMode(this.id, {
                selectedMode: (modePart.data.selectedMode || 'auto') as any,
                resolvedMode: modePart.data.resolvedMode as any,
                reason: modePart.data.reason,
              })
            }
          } catch (e) {
            console.debug('[Chat Store] Unable to parse data-mode part on finish')
          }

          // formating value of content field
          this.messages = this.messages.map(message => ({...message,content: message.content || message.parts || ''}));

          // Save messages to Pinia store (local state) using captured store reference).__PINIA__?.state?.value?.chat
          store.saveChatMessages(this.id, this.messages);
          
          // Persist messages to database
          try {
            const { useChatPersistence } = await import('@/composables/useChatPersistence')
            const { saveMessages } = useChatPersistence()
            await saveMessages(this.id, this.messages, assistantId)
            console.log(`[Chat Store] Messages persisted to database for chat ${this.id}`)
          } catch (error) {
            console.error('[Chat Store] Failed to persist messages to database:', error)
            // Continue even if persistence fails - messages are still in Pinia store
          }
          
          // Generate title after first exchange (user message + assistant response)
          const userMessages = this.messages.filter(msg => msg.role === 'user');
          const assistantMessages = this.messages.filter(msg => msg.role === 'assistant');
          
          // Trigger title generation if this is the first complete exchange
          if (userMessages.length === 1 && assistantMessages.length >= 1) {
            console.log('Triggering title generation for first message exchange');
            // Use setTimeout to avoid blocking the UI
            setTimeout(() => {
              store.generateChatTitle(this.id, assistantId);
            }, 100);
          }
        }
      });

      return chatInstance;
    },

    // ==================== CHAT CONTENT OPERATIONS ====================
    
    // Save chat messages for a specific chat ID
    saveChatMessages(id: string, messages: Message[]) {
      console.log("Save chat messages", id);
      
      const chat = this.chats.find(c => c.id === id);
      if (chat) {
        chat.messages = [...messages]; // Ensure reactivity
        chat.updatedAt = new Date(); // Update the timestamp
      }
    },

    // Clear chat history for a specific chat ID
    async clearChatHistory(id: string) {
      console.log("Clearing chat history", id);
      const chat = this.chats.find(c => c.id === id);
      if (!chat) return;

      console.log("Clear chat history", chat);
      
      // Optimistically clear messages from ChatData object
      chat.messages = [];
      chat.updatedAt = new Date();
      
      // Also clear the Chat instance if it exists
      const chatInstance = this.activeChatInstances.get(id);
      if (chatInstance && chatInstance.state) {
        chatInstance.state.messages = [];
        if (chatInstance.state.messagesRef) {
          chatInstance.state.messagesRef.length = 0; // Clear array while maintaining reference
        }
      }

      // Persist the clearing to the database
      try {
        await $fetch(`/api/chats/${id}/clear`, {
          method: 'POST'
        })
        console.log(`[Chat Store] Successfully cleared chat ${id} messages from database`)
        // Also clear resolved mode info since context is reset
        if (this.resolvedModeByChat[id]) {
          delete this.resolvedModeByChat[id]
        }
        const router = useRouter()
        const route = useRoute()
        let routePath = route.path
        await router.push(routePath.replace(id, 'start'))
      } catch (error) {
        console.error(`[Chat Store] Failed to clear chat ${id} messages from database:`, error)
        // Note: We don't rollback the optimistic update since the user expects it to be cleared locally
        // The inconsistency will be resolved on next page refresh when messages are loaded from DB
        throw error
      }
    },

    // ==================== CHAT RENAME OPERATIONS ====================
    
    // Start renaming a chat
    startChatRename(id: string) {
      this.renamingChatId = id;
    },

    // Update chat label (optimistic) and persist to backend
    async updateChatLabel(id: string, label: string) {
      const chat = this.chats.find(c => c.id === id);
      const previous = chat ? { label: chat.label, updatedAt: chat.updatedAt } : null
      if (chat) {
        // Optimistic update
        chat.label = label;
        chat.updatedAt = new Date();
      }
      this.renamingChatId = null;

      // Persist to server (best-effort)
      try {
        await $fetch(`/api/chats/${id}`, {
          method: 'PATCH',
          body: { label }
        })
      } catch (err) {
        console.error('[Chat Store] Failed to persist chat label:', err)
        // Optional: revert on failure
        if (chat && previous) {
          chat.label = previous.label
          chat.updatedAt = previous.updatedAt
        }
      }
    },

    // Cancel chat rename operation
    cancelChatRename() {
      this.renamingChatId = null;
    },

    // ==================== OTHER CHAT OPERATIONS ====================
    
    // Generate and update chat title based on first message
    async generateChatTitle(chatId: string, assistantId: string) {
      const chat = this.getChat(chatId);
      if (!chat || chat.messages.length === 0) {
        console.warn(`Cannot generate title: chat ${chatId} not found or has no messages`);
        return;
      }

      // Only generate title if it's still the default label (e.g., "New Chat", "New Chat 2") and has user messages
      const hasUserMessage = chat.messages.some(msg => msg.role === 'user');
      const isDefaultLabel = !chat.label || /^New Chat(\s*\d+)?$/i.test(String(chat.label).trim());
      if (!hasUserMessage || !isDefaultLabel) {
        return; // Already has a custom title or no user messages
      }

      try {
        const firstUserMessage = chat.messages.find(msg => msg.role === 'user');
        if (!firstUserMessage) {
          return;
        }
        const hasText = typeof firstUserMessage.content === 'string'
          ? firstUserMessage.content.trim().length > 0
          : Array.isArray((firstUserMessage as any).parts)
            ? Boolean((firstUserMessage as any).parts.find((p: any) => p?.type === 'text' && (p.text || '').trim().length > 0))
            : false
        if (!hasText) {
          return;
        }

        // Call the chat-specific title generation API (persists to DB)
        const response = await $fetch(`/api/chats/${chatId}/title/generate`, {
          method: 'POST'
        });

        if ((response as any).success && (response as any).title) {
          // Update the chat label with the generated title
          const title = (response as any).title as string
          const updated = (response as any).updated
          if (updated !== false) {
            this.updateChatLabel(chatId, title);
            console.log(`Generated title for chat ${chatId}: "${title}" (persisted)`);
          } else {
            console.log(`Generated title for chat ${chatId}: "${title}" (not persisted; existing custom label kept)`);
          }
        }
      } catch (error) {
        console.error(`Error generating title for chat ${chatId}:`, error);
      }
    },
    
    // Share a chat (server-backed)
    async shareChat(id: string): Promise<{ success: boolean; token?: string; error?: string }> {
      try {
        // Ensure session exists in DB before attempting to share
        const chat = this.getChat(id)
        if (chat && chat.assistantId) {
          try {
            await $fetch(`/api/chats/${id}/session`, {
              method: 'POST',
              body: {
                chatId: id,
                assistantId: chat.assistantId,
                label: chat.label,
                status: chat.status,
                createdAt: (chat.createdAt instanceof Date ? chat.createdAt : new Date(chat.createdAt as any)).toISOString(),
                updatedAt: new Date().toISOString(),
              },
            })
          } catch (e) {
            // Best-effort; proceed to share even if upsert fails
            console.warn('[Chat Store] Preflight session upsert failed (continuing to share):', e)
          }
        }
        const res = await $fetch<{ success: boolean; token?: string }>(`/api/chats/${id}/share`, {
          method: 'POST',
        })
        if (res?.success && res.token) {
          return { success: true, token: res.token }
        }
        return { success: false, error: 'Unknown error' }
      } catch (err: any) {
        console.error('shareChat failed:', err)
        return { success: false, error: err?.data?.message || err?.message || 'Failed to share chat' }
      }
    },
    
    // Archive a chat (server-backed)
    async archiveChat(id: string): Promise<{ success: boolean; error?: string }> {
      try {
        const res = await $fetch<{ success: boolean }>(`/api/chats/${id}/archive`, {
          method: 'POST',
        })
        if (res?.success) {
          const chat = this.chats.find(c => c.id === id)
          if (chat) {
            chat.status = 'archived'
            chat.updatedAt = new Date()
          }
          return { success: true }
        }
        return { success: false, error: 'Unknown error' }
      } catch (err: any) {
        console.error('archiveChat failed:', err)
        return { success: false, error: err?.data?.message || err?.message || 'Failed to archive chat' }
      }
    },
    
    // Export a chat in different formats
    exportChat(id: string, format: 'json' | 'txt' | 'md' = 'json') {
      const chat = this.getChat(id);
      if (!chat) {
        console.error(`Chat ${id} not found`);
        return null;
      }
      
      try {
        let content = '';
        let filename = '';

        switch (format) {
          case 'json':
            content = JSON.stringify(chat, null, 2);
            filename = `chat-${chat.label || 'export'}-${new Date().toISOString().split('T')[0]}.json`;
            break;
          case 'txt':
            content = chat.messages.map(msg => `${msg.role}: ${getTextFromMessage(msg)}`).join('\n\n');
            filename = `chat-${chat.label || 'export'}-${new Date().toISOString().split('T')[0]}.txt`;
            break;
          case 'md':
            content = chat.messages.map(msg => `## ${msg.role.charAt(0).toUpperCase() + msg.role.slice(1)}\n\n${getTextFromMessage(msg)}`).join('\n\n---\n\n');
            filename = `chat-${chat.label || 'export'}-${new Date().toISOString().split('T')[0]}.md`;
            break;
        }
        
        // Create and trigger download
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        return { filename, success: true };
      } catch (error) {
        console.error('Export failed:', error);
        return null;
      }
    },

    // ==================== PINNED CHAT OPERATIONS ====================
    
    // Check if a chat is pinned
    isPinned(id: string): boolean {
      const chat = this.chats.find(c => c.id === id)
      return chat?.pinned || false
    },

    // Get all pinned chats
    getPinnedChats(): ChatData[] {
      // Ensure initialized before returning data
      this.ensureInitializedSync()
      return this.chats.filter(chat => chat.pinned && chat.status !== 'archived').sort((a, b) => 
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      )
    },

    // Get unpinned chats
    getUnpinnedChats(): ChatData[] {
      // Ensure initialized before returning data
      this.ensureInitializedSync()
      return this.chats.filter(chat => !chat.pinned && chat.status !== 'archived').sort((a, b) => 
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      )
    },

    // Synchronous initialization trigger (for immediate UI needs)
    ensureInitializedSync() {
      if (!this.isInitialized && !this.isInitializing) {
        // Trigger async initialization but don't wait for it
        this.ensureInitialized().catch(error => {
          console.error('[Chat Store] Background sync initialization failed:', error)
        })
      }
    },

    // Insert chat session into database
    async _persistChatSession(chatData: ChatData) {
      try {
        const now = new Date()
        const response = await $fetch(`/api/chats/${chatData.id}/session`, {
          method: 'POST',
          body: {
            chatId: chatData.id,
            assistantId: chatData.assistantId,
            label: chatData.label,
            status: chatData.status,
            createdAt: now.toISOString(),
            updatedAt: now.toISOString()
          }
        })

        return response
      } catch (error) {
        console.error('[Chat Store] Failed to create chat session in database:', error)
        throw error // Re-throw so caller can handle
      }
    }
  },
  persist: false, // Disable localStorage persistence completely
});
